// ─────────────────────────────────────────────────────────────
//  AboutData.js  —  All constants for the About Us page
//  TheVisaCo · Punjabi Bagh, New Delhi
// ─────────────────────────────────────────────────────────────

export const BRAND = {
  name: "TheVisaCo",
  tagline: "Zero Worry Visa",
  description:
    "TheVisaCo is a full-stack visa consultation and management platform built for Indian travellers. From exploring visa requirements to booking appointments and tracking applications — everything happens in one place.",
  location: "Punjabi Bagh West, New Delhi – 110026",
  email: "support@thevisa.co",
  phone: "+91 98100 44015",
  hours: "Mon–Sat · 9 AM – 7 PM",
};

// ── Stats ─────────────────────────────────────────────────────
export const STATS = [
  { value: "195+",   label: "Countries Covered"    },
  { value: "12+",    label: "Platform Modules"      },
  { value: "80K+",   label: "Monthly Travellers"    },
  // { value: "24/7",   label: "Platform Access"       },
];

// ── Section Copy ──────────────────────────────────────────────
export const HERO_TITLE        = "Built to Make Every Visa Application Stress-Free";
export const FEATURES_SUBTITLE = "Twelve fully-featured modules — each solving a distinct concern in your visa journey.";
export const TEAM_SUBTITLE     = "A focused team of product thinkers, legal minds, and builders working to remove every friction point from the Indian travel experience.";

export const MISSION =
  "To eliminate visa anxiety for every Indian traveller by delivering accurate information, smart tooling, and expert consultation — all in one beautifully designed platform.";

// ── Story ─────────────────────────────────────────────────────
export const STORY_PARAGRAPHS = [
  "It started with a missed trip. Our founder spent three weekends gathering documents, calling embassies, and navigating outdated government portals — only to have a Schengen appointment slip away due to a paperwork error no one warned him about.",
  "That frustration became the brief: build the platform we wished had existed. Something accurate, navigable, and complete — from first search to stamped passport.",
  "We launched in 2023 from a small office in Punjabi Bagh, New Delhi. Today TheVisaCo serves thousands of Indian travellers, with modules covering 195+ countries, real-time appointment slots, document checklists, and direct consultation with certified advisors.",
];

// ── Timeline — extended for horizontal auto-scroll ────────────
export const TIMELINE = [
  {
    year: "2022",
    month: "Oct",
    title: "The Missed Trip",
    body: "Arjun misses a Schengen appointment due to a form error. The idea for TheVisaCo is born in a coffee shop in Karol Bagh.",
    image: "https://images.unsplash.com/photo-1517760444937-f6397edcbbcd?w=600&q=80&auto=format&fit=crop",
  },
  {
    year: "2023",
    month: "Feb",
    title: "First Build",
    body: "Priya joins as co-founder. The first working prototype — a single-page Schengen checker — goes live to 50 beta users.",
    image: "https://images.unsplash.com/photo-1498050108023-c5249f4df085?w=600&q=80&auto=format&fit=crop",
  },
  {
    year: "2023",
    month: "Jul",
    title: "Official Launch",
    body: "TheVisaCo launches publicly from Punjabi Bagh, New Delhi. 1,200 users in the first week. Coverage expanded to 60 countries.",
    image: "https://images.unsplash.com/photo-1556761175-4b46a572b786?w=600&q=80&auto=format&fit=crop",
  },
  {
    year: "2024",
    month: "Jan",
    title: "12 Modules Live",
    body: "Full platform launched — Schengen Checker, Visa Map, Shopping Mart, Mock Interview, and 8 more modules all go live simultaneously.",
    image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=600&q=80&auto=format&fit=crop",
  },
  {
    year: "2024",
    month: "May",
    title: "50K Users",
    body: "Platform crosses 50,000 monthly active travellers. First enterprise white-label deal signed with a top Mumbai travel agency.",
    image: "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=600&q=80&auto=format&fit=crop",
  },
  {
    year: "2024",
    month: "Sep",
    title: "80K Monthly Users",
    body: "Expanding team to 14 people. White-label version launched. Platform crosses 80,000 monthly active travellers.",
    image: "https://images.unsplash.com/photo-1521737604893-d14cc237f11d?w=600&q=80&auto=format&fit=crop",
  },
  {
    year: "2025",
    month: "Jan",
    title: "195 Countries",
    body: "Full country coverage achieved. Every destination reachable by Indian passport holders now has live data, checklists and embassy contacts.",
    image: "https://images.unsplash.com/photo-1524661135-423995f22d0b?w=600&q=80&auto=format&fit=crop",
  },
  {
    year: "2025",
    month: "Jun",
    title: "AI Advisory Beta",
    body: "Beta launch of AI-powered document review — auto-flagging errors before submission. Early testers report 40% fewer rejection requests.",
    image: "https://images.unsplash.com/photo-1677442135703-1787eea5ce01?w=600&q=80&auto=format&fit=crop",
  },
];

// ── Values ────────────────────────────────────────────────────
export const VALUES = [
  {
    id: "accuracy",
    icon: "✦",
    title: "Accuracy above all",
    description:
      "Visa rules change without notice. We monitor 195+ countries daily so our data is never stale — because one wrong field on a form can cost you a trip.",
    image: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=600&q=80&auto=format&fit=crop",
  },
  {
    id: "empathy",
    icon: "◎",
    title: "Design with empathy",
    description:
      "Every screen, every step, every piece of copy is written as though a first-time traveller will see it. Clarity is not a nicety; it is the product.",
    image: "https://images.unsplash.com/photo-1522542550221-31fd19575a2d?w=600&q=80&auto=format&fit=crop",
  },
  {
    id: "transparency",
    icon: "◈",
    title: "Transparent pricing",
    description:
      "We show every government fee, service charge, and courier cost upfront. No surprises at checkout. No hidden markups on official fees.",
    image: "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=600&q=80&auto=format&fit=crop",
  },
  {
    id: "speed",
    icon: "◐",
    title: "Speed that respects your time",
    description:
      "Appointment slots disappear in minutes. Our checker runs live against consulate calendars so you never miss a window you didn't know had opened.",
    image: "https://images.unsplash.com/photo-1495364141860-b0d03eccd065?w=600&q=80&auto=format&fit=crop",
  },
  {
    id: "reliability",
    icon: "◆",
    title: "Reliability you can trust",
    description:
      "Every piece of data is verified against official government sources. If we don't know, we tell you we don't know — no guesswork, no outdated info.",
    image: "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=600&q=80&auto=format&fit=crop",
  },
  {
    id: "support",
    icon: "♢",
    title: "Support that cares",
    description:
      "Real humans, real answers. Our support team is staffed by trained visa advisors who have helped thousands navigate complex applications across 195+ countries.",
    image: "https://images.unsplash.com/photo-1552581234-26160f608093?w=600&q=80&auto=format&fit=crop",
  },
];

// ── Features / Modules ────────────────────────────────────────
export const FEATURES = [
  {
    id: "home",
    route: "/",
    title: "Home & Destination Explorer",
    description:
      "Dynamic destination cards, popularity filters, region filters, and a built-in contact form with Google Calendar integration.",
    tags: ["React", "Filter", "Contact Form"],
    image: "https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=600&q=80&auto=format&fit=crop",
    color: "#E8F4FD",
  },
  {
    id: "visa-detail",
    route: "/Detail",
    title: "Visa Detail Centre",
    description:
      "Deep-dive visa info for any country — embassy locations, VFS/BLS office addresses, consulate forms, and alias-powered country search.",
    tags: ["Embassies", "Offices", "Alias Search"],
    image: "https://images.unsplash.com/photo-1568096889942-6eedde686635?w=600&q=80&auto=format&fit=crop",
    color: "#FDF3E8",
  },
  {
    id: "requirements",
    route: "/requirements",
    title: "Visa Requirements Checker",
    description:
      "Select your passport country and destination to instantly see visa-on-arrival, e-visa, or sticker visa requirements.",
    tags: ["Requirements", "Country Search"],
    image: "https://images.unsplash.com/photo-1532938911079-1b06ac7ceec7?w=600&q=80&auto=format&fit=crop",
    color: "#EDF8F0",
  },
  {
    id: "schengen",
    route: "/SchenegenChecker",
    title: "Schengen Appointment Checker",
    description:
      "A 7-step guided flow: search available slots · pick appointment date · fill traveller details · choose sponsor · pay.",
    tags: ["Multi-step Flow", "Calendar", "Payments"],
    image: "https://images.unsplash.com/photo-1467269204594-9661b134dd2b?w=600&q=80&auto=format&fit=crop",
    color: "#F3EDFD",
  },
  {
    id: "govtfee",
    route: "/GovtFeeHistory",
    title: "Government Fee History",
    description:
      "Browse and filter official government visa fee records by country with alias search support and real-time ₹ formatting.",
    tags: ["Fee Data", "Filter", "Currency"],
    image: "https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=600&q=80&auto=format&fit=crop",
    color: "#FEF3ED",
  },
  {
    id: "shopping",
    route: "/ShoppingMart",
    title: "Visa Shopping Mart",
    description:
      "A product marketplace for visa-related services — insurance, attestation, translation, photo sets — with cart and checkout.",
    tags: ["E-Commerce", "Auth", "Cart"],
    image: "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=600&q=80&auto=format&fit=crop",
    color: "#FDF0F5",
  },
  {
    id: "interview",
    route: "/interview",
    title: "US Mock Interview",
    description:
      "Interactive mock visa interview simulator for US B1/B2 visa applicants. Covers question sets, scoring, tips, and prep guides.",
    tags: ["Interview Prep", "Scoring"],
    image: "https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=600&q=80&auto=format&fit=crop",
    color: "#EDF5FE",
  },
  {
    id: "profile",
    route: "/profile",
    title: "User Profile & Applications",
    description:
      "Authenticated dashboard with application tracking, document management, and rewards system with milestones.",
    tags: ["Auth", "Rewards", "Documents"],
    image: "https://images.unsplash.com/photo-1507537297725-24a1293cf967?w=600&q=80&auto=format&fit=crop",
    color: "#EDFAF5",
  },
  {
    id: "passport",
    route: "/Passportguide",
    title: "Passport Guide",
    description:
      "Step-by-step guide for fresh passport applications and renewals — documentation checklists, Seva Kendra locator, timeline estimator.",
    tags: ["Guide", "Checklist"],
    image: "https://images.unsplash.com/photo-1544947950-fa07a98d237f?w=600&q=80&auto=format&fit=crop",
    color: "#FEF9E8",
  },
  {
    id: "events",
    route: "/EventsPage",
    title: "Events Page",
    description:
      "Visa-related events calendar — embassy open days, document drives, consulate info sessions. Filter by city and event type.",
    tags: ["Events", "Calendar"],
    image: "https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=600&q=80&auto=format&fit=crop",
    color: "#F5EDFD",
  },
  {
    id: "map",
    route: "/VisaMap",
    title: "Visa Map",
    description:
      "Interactive world map visualising visa-free, visa-on-arrival, and e-visa accessible countries for Indian passport holders.",
    tags: ["Map", "Visualisation"],
    image: "https://images.unsplash.com/photo-1524661135-423995f22d0b?w=600&q=80&auto=format&fit=crop",
    color: "#E8F6FD",
  },
  {
    id: "career",
    route: "/VisaCareer",
    title: "Visa Career Portal",
    description:
      "Job listings and internship opportunities within the visa and immigration consultancy space. Apply directly from the platform.",
    tags: ["Jobs", "Careers"],
    image: "https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=600&q=80&auto=format&fit=crop",
    color: "#F0F8EE",
  },
];

// ── Popular Destination Countries ─────────────────────────────
export const POPULAR_COUNTRIES = [
  { name: "France",         flag: "🇫🇷", image: "https://images.unsplash.com/photo-1502602898657-3e91760cbb34?w=500&q=80&auto=format&fit=crop", visaType: "Sticker Visa" },
  { name: "UAE",            flag: "🇦🇪", image: "https://images.unsplash.com/photo-1546412414-e1885259563a?w=500&q=80&auto=format&fit=crop", visaType: "Visa on Arrival" },
  { name: "United Kingdom", flag: "🇬🇧", image: "https://images.unsplash.com/photo-1513635269975-59663e0ac1ad?w=500&q=80&auto=format&fit=crop", visaType: "Sticker Visa" },
  { name: "Thailand",       flag: "🇹🇭", image: "https://images.unsplash.com/photo-1552465011-b4e21bf6e79a?w=500&q=80&auto=format&fit=crop", visaType: "Visa on Arrival" },
  { name: "USA",            flag: "🇺🇸", image: "https://images.unsplash.com/photo-1534430480872-3498386e7856?w=500&q=80&auto=format&fit=crop", visaType: "Sticker Visa" },
  { name: "Japan",          flag: "🇯🇵", image: "https://images.unsplash.com/photo-1540959733332-eab4deabeeaf?w=500&q=80&auto=format&fit=crop", visaType: "Sticker Visa" },
  { name: "Singapore",      flag: "🇸🇬", image: "https://images.unsplash.com/photo-1565967511849-76a60a516170?w=500&q=80&auto=format&fit=crop", visaType: "Visa Free" },
  { name: "Canada",         flag: "🇨🇦", image: "https://images.unsplash.com/photo-1517935706615-2717063c2225?w=500&q=80&auto=format&fit=crop", visaType: "eTA" },
  { name: "Italy",          flag: "🇮🇹", image: "https://images.unsplash.com/photo-1515542622106-078bda13f1a0?w=500&q=80&auto=format&fit=crop", visaType: "Sticker Visa" },
  { name: "Australia",      flag: "🇦🇺", image: "https://images.unsplash.com/photo-1506973035872-a4ec16b8e8d9?w=500&q=80&auto=format&fit=crop", visaType: "e-Visa" },
  { name: "Germany",        flag: "🇩🇪", image: "https://images.unsplash.com/photo-1467269204594-9661b134dd2b?w=500&q=80&auto=format&fit=crop", visaType: "Sticker Visa" },
  { name: "Malaysia",       flag: "🇲🇾", image: "https://images.unsplash.com/photo-1596422846543-75c6fc197f07?w=500&q=80&auto=format&fit=crop", visaType: "Visa Free" },
  { name: "Spain",          flag: "🇪🇸", image: "https://images.unsplash.com/photo-1543783207-ec64e4d95325?w=500&q=80&auto=format&fit=crop", visaType: "Sticker Visa" },
  { name: "Maldives",       flag: "🇲🇻", image: "https://images.unsplash.com/photo-1573843981267-be1999ff37cd?w=500&q=80&auto=format&fit=crop", visaType: "Visa on Arrival" },
  { name: "New Zealand",    flag: "🇳🇿", image: "https://images.unsplash.com/photo-1507699622108-4be3abd695ad?w=500&q=80&auto=format&fit=crop", visaType: "e-Visa" },
  { name: "Switzerland",    flag: "🇨🇭", image: "https://images.unsplash.com/photo-1530122037265-a5f1f91d3b99?w=500&q=80&auto=format&fit=crop", visaType: "Sticker Visa" },
];

// ── Team ──────────────────────────────────────────────────────
export const TEAM_CEOS = [
  {
    id: "ceo",
    name: "Arjun Mehta",
    role: "Co-Founder & CEO",
    bio: "Former travel-tech PM at MakeMyTrip. Built TheVisaCo after spending three weekends on a single Schengen application.",
    funFact: "Has visited 31 countries — with a visa rejection story from each of the first 4.",
    image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=600&q=80&auto=format&fit=crop&crop=faces",
    linkedin: "#",
    location: "New Delhi",
    stat1Label: "Countries Visited",
    stat1Value: "31",
    stat2Label: "Years in Travel Tech",
    stat2Value: "9",
  },
  {
    id: "cto",
    name: "Priya Nair",
    role: "Co-Founder & CTO",
    bio: "Full-stack engineer with 9 years at Razorpay and Atlassian. Leads architecture, data pipelines, and the real-time appointment engine.",
    funFact: "Once stayed up 38 hours to ship the Schengen checker before a German embassy rule change.",
    image: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=600&q=80&auto=format&fit=crop&crop=faces",
    linkedin: "#",
    location: "Bengaluru",
    stat1Label: "Systems Built",
    stat1Value: "40+",
    stat2Label: "Years Engineering",
    stat2Value: "9",
  },
];

// ── 6 Department Leads ────────────────────────────────────────
export const TEAM_LEADS = [
  {
    id: "product",
    name: "Rahul Sharma",
    role: "Head of Product",
    department: "Product",
    bio: "Shapes every feature roadmap. Previously at Swiggy and Zomato.",
    funFact: "Can plan a 15-day multi-country itinerary in under 4 minutes.",
    linkedin: "#",
    image: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=500&q=80&auto=format&fit=crop&crop=faces",
  },
  {
    id: "design",
    name: "Sneha Kapoor",
    role: "Lead Designer",
    department: "Design",
    bio: "Designed TheVisaCo's entire component library from scratch. Former design lead at Dunzo.",
    funFact: "Has redesigned the visa application flow 11 times — and counting.",
    linkedin: "#",
    image: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=500&q=80&auto=format&fit=crop&crop=faces",
  },
  {
    id: "legal",
    name: "Vikram Anand",
    role: "Head of Visa Advisory",
    department: "Legal & Compliance",
    bio: "Certified immigration consultant with 14 years and 60+ embassies under his belt.",
    funFact: "Has personally handled 3,000+ visa applications with a 97% success rate.",
    linkedin: "#",
    image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=500&q=80&auto=format&fit=crop&crop=faces",
  },
  {
    id: "growth",
    name: "Meera Joshi",
    role: "Head of Growth",
    department: "Marketing",
    bio: "Scaled the platform from 500 to 80,000 monthly users in 18 months without paid acquisition.",
    funFact: "Once ran a marketing campaign that went viral — and crashed our servers twice.",
    linkedin: "#",
    image: "https://images.unsplash.com/photo-1580489944761-15a19d654956?w=500&q=80&auto=format&fit=crop&crop=faces",
  },
  {
    id: "eng",
    name: "Karan Bose",
    role: "Senior Engineer",
    department: "Engineering",
    bio: "Built the Schengen appointment engine and real-time fee sync system.",
    funFact: "Wrote the first version of the Schengen checker in a single weekend — on 3 hours of sleep.",
    linkedin: "#",
    image: "https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?w=500&q=80&auto=format&fit=crop&crop=faces",
  },
  {
    id: "ops",
    name: "Ananya Singh",
    role: "Operations Lead",
    department: "Operations",
    bio: "Keeps 195+ country records accurate. Coordinates directly with embassy networks and VFS Global.",
    funFact: "Has embassy contacts on speed dial for 23 countries — and counting.",
    linkedin: "#",
    image: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=500&q=80&auto=format&fit=crop&crop=faces",
  },
];

// ── Section headings used in JSX ────────────────────────────
export const STORY_HEADING = "How it all began";
export const STORY_BADGE_YEAR = "2023";
export const STORY_BADGE_TEXT = "Founded in New Delhi";
export const MISSION_HEADING = "Why we do this";
export const VALUES_HEADING = "What we stand for";
export const VALUES_SUBTITLE = "Every product decision, every data update, every support reply is filtered through these four principles.";
export const TIMELINE_HEADING = "The journey so far";
export const TIMELINE_SUBTITLE = "From a missed Schengen appointment in 2022 to 80,000 monthly travellers — every milestone on one line.";
export const FEATURES_HEADING = "Everything in one platform";
export const TEAM_HEADING = "Experts Who Make It Happen";
export const COUNTRIES_HEADING = "Top destinations for Indian travellers";
export const COUNTRIES_SUBTITLE = "Full visa info, document checklists, fee history, and embassy contacts — for every country below and 187 more.";

// ── Photo Strip ──────────────────────────────────────────────
export const PHOTO_STRIP = [
  { src: "https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=900&q=80&auto=format&fit=crop", alt: "Traveller at airport" },
  { src: "https://images.unsplash.com/photo-1530521954074-e64f6810b32d?w=600&q=80&auto=format&fit=crop", alt: "World map" },
  { src: "https://images.unsplash.com/photo-1521737604893-d14cc237f11d?w=600&q=80&auto=format&fit=crop", alt: "Office team" },
];

// ── Derived ─────────────────────────────────────────────────
export const ALL_TEAM = [...TEAM_CEOS, ...TEAM_LEADS];

// ── Images ────────────────────────────────────────────────────
export const STORY_IMAGE   = "https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=900&q=80&auto=format&fit=crop";